import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Calendar,
  CheckCircle,
  AlertCircle,
  XCircle,
  CheckSquare,
  Square,
  Zap,
  Loader2,
  Shield,
  Scale,
  DollarSign,
  X,
  Settings
} from 'lucide-react';
import { pagesAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const PagesManagement = () => {
  const { notify } = useNotificationHook();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagesPerPage] = useState(10);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('pages');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPage, setNewPage] = useState({
    page_type: '',
    title: '',
    subtitle: '',
    description: '',
    content: {}
  });
  const [editingPage, setEditingPage] = useState(null);

  // Bulk actions state
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const pageTypes = [
    { value: 'not-found', label: '404 Not Found', icon: AlertCircle },
    { value: 'coming-soon', label: 'Coming Soon', icon: Calendar },
    { value: 'maintenance', label: 'Maintenance Mode', icon: Zap },
    { value: 'privacy-policy', label: 'Privacy Policy', icon: Shield },
    { value: 'terms-conditions', label: 'Terms & Conditions', icon: Scale },
    { value: 'refund-policy', label: 'Refund Policy', icon: DollarSign }
  ];

  const fetchPages = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching pages...');
      
      const response = await pagesAPI.getAll();
      console.log('✅ Pages response:', response);
      
      if (response?.data?.success && Array.isArray(response.data.data)) {
        setPages(response.data.data);
      } else {
        console.warn('Invalid response format:', response);
        setPages([]);
      }
    } catch (error) {
      console.error('❌ Error fetching pages:', error);
      notify.error('Failed to fetch pages');
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const handleAddPage = async () => {
    try {
      if (!newPage.page_type || !newPage.title) {
        notify.error('Page type and title are required');
        return;
      }

      const response = await pagesAPI.create(newPage);
      if (response?.data?.success) {
        notify.success('Page added successfully');
        setShowAddForm(false);
        setNewPage({
          page_type: '',
          title: '',
          subtitle: '',
          description: '',
          content: {}
        });
        fetchPages();
      }
    } catch (error) {
      console.error('Error adding page:', error);
      notify.error(error.response?.data?.message || 'Failed to add page');
    }
  };

  const handleUpdatePage = async () => {
    try {
      if (!editingPage.title) {
        notify.error('Title is required');
        return;
      }

      const response = await pagesAPI.update(editingPage.id, editingPage);
      if (response?.data?.success) {
        notify.success('Page updated successfully');
        setEditingPage(null);
        fetchPages();
      }
    } catch (error) {
      console.error('Error updating page:', error);
      notify.error(error.response?.data?.message || 'Failed to update page');
    }
  };

  const handleDeletePage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) {
      return;
    }

    try {
      const response = await pagesAPI.delete(id);
      if (response?.data?.success) {
        notify.success('Page deleted successfully');
        fetchPages();
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      notify.error('Failed to delete page');
    }
  };

  const clearMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const getPageIcon = (pageType) => {
    const pageTypeObj = pageTypes.find(pt => pt.value === pageType);
    return pageTypeObj ? pageTypeObj.icon : FileText;
  };

  const getPageLabel = (pageType) => {
    const pageTypeObj = pageTypes.find(pt => pt.value === pageType);
    return pageTypeObj ? pageTypeObj.label : pageType;
  };

  const renderContentEditor = (content, onChange) => {
    if (!content) return null;

    const handleContentChange = (key, value) => {
      onChange({ ...content, [key]: value });
    };

    return (
      <div className="space-y-4">
        {Object.keys(content).map((key) => {
          const value = content[key];
          
          if (typeof value === 'string') {
            return (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                </label>
                <textarea
                  value={value}
                  onChange={(e) => handleContentChange(key, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  rows="3"
                />
              </div>
            );
          } else if (Array.isArray(value)) {
            return (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                </label>
                <textarea
                  value={JSON.stringify(value, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleContentChange(key, parsed);
                    } catch (error) {
                      // Invalid JSON, keep as string
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                  rows="6"
                  placeholder="Enter JSON array"
                />
              </div>
            );
          } else if (typeof value === 'object' && value !== null) {
            return (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                </label>
                <textarea
                  value={JSON.stringify(value, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleContentChange(key, parsed);
                    } catch (error) {
                      // Invalid JSON, keep as string
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                  rows="8"
                  placeholder="Enter JSON object"
                />
              </div>
            );
          }
          
          return null;
        })}
      </div>
    );
  };

  const renderPageForm = (page, isEditing = false) => {
    const currentPage = isEditing ? page : newPage;
    const setCurrentPage = isEditing ? setEditingPage : setNewPage;

    return (
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Page Type
            </label>
            <select
              value={currentPage.page_type}
              onChange={(e) => setCurrentPage({ ...currentPage, page_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={isEditing}
            >
              <option value="">Select Page Type</option>
              {pageTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Title
            </label>
            <input
              type="text"
              value={currentPage.title}
              onChange={(e) => setCurrentPage({ ...currentPage, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Enter page title"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Subtitle
          </label>
          <input
            type="text"
            value={currentPage.subtitle || ''}
            onChange={(e) => setCurrentPage({ ...currentPage, subtitle: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="Enter page subtitle"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={currentPage.description || ''}
            onChange={(e) => setCurrentPage({ ...currentPage, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            rows="3"
            placeholder="Enter page description"
          />
        </div>

        {/* Content Tabs */}
        <div>
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'general', label: 'General Content', icon: FileText },
                { id: 'content', label: 'Advanced Content', icon: Settings }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="mt-4">
            {activeTab === 'general' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Last Updated
                  </label>
                  <input
                    type="text"
                    value={currentPage.last_updated || ''}
                    onChange={(e) => setCurrentPage({ ...currentPage, last_updated: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., December 1, 2024"
                  />
                </div>
              </div>
            )}

            {activeTab === 'content' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Page Content (JSON)
                </label>
                <textarea
                  value={JSON.stringify(currentPage.content || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setCurrentPage({ ...currentPage, content: parsed });
                    } catch (error) {
                      // Invalid JSON, keep as string
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
                  rows="15"
                  placeholder="Enter JSON content"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Enter valid JSON content for the page. This will be used to populate dynamic content on the frontend.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              Pages Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage dynamic pages and their content
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Page
          </button>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <div className="flex justify-between items-center">
            <span>{message.text}</span>
            <button onClick={clearMessage} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Add Page Form */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add New Page</h3>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {renderPageForm(newPage, false)}
          
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAddPage}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add Page
            </button>
          </div>
        </div>
      )}

      {/* Pages List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Dynamic Pages ({pages.length})
          </h3>
          
          {pages.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No pages found. Add your first page to get started.
            </p>
          ) : (
            <div className="space-y-4">
              {pages.map((page) => {
                const PageIcon = getPageIcon(page.page_type);
                return (
                  <div
                    key={page.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    {editingPage?.id === page.id ? (
                      // Edit Form
                      <div className="space-y-4">
                        {renderPageForm(editingPage, true)}
                        
                        <div className="flex justify-end gap-3">
                          <button
                            onClick={() => setEditingPage(null)}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUpdatePage}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Display Mode
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <PageIcon className="w-8 h-8 text-primary-600" />
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {page.title}
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Type: {getPageLabel(page.page_type)} • Updated: {new Date(page.last_updated).toLocaleDateString()}
                            </p>
                            {page.subtitle && (
                              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                                {page.subtitle}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditingPage(page)}
                            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
                            title="Edit page"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePage(page.id)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg"
                            title="Delete page"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PagesManagement; 